import React, { useState, useEffect, useContext } from 'react'
import stringComparison from 'string-comparison'
//import { levenshtein } from "string-comparison"
import { useSignal } from "@preact/signals-react"
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import Snap from 'snapsvg-cjs'
import { StoreContext } from "../../context/StoreContext"
import Utility from "../../objects/Utility"
import './SheetOperations.scss'

const SheetOperations = ({ sheetParameters, changeSheetMenuOperationsOpened, changeGuideType, changePrintableArea }) => {
    const controller = new AbortController();
    const { state, actions } = useContext(StoreContext)
    const [sheetOperationsOpen, setSheetOperationsOpen] = useState(false)
    const [saveOption, setSaveOption] = useState('app')
    const [saveName, setSaveName] = useState('')
    const [saveActionsResult, setSaveActionsResult] = useState('')
    const sgLastSentGuideChange = useSignal(false)
    const [printableAreaMM_width, setPrintableAreaMM_width] = useState(sheetParameters.printableAreaMM[0])
    const [printableAreaMM_height, setPrintableAreaMM_height] = useState(sheetParameters.printableAreaMM[1])
    const [buildSheetPaper, setBuildSheetPaper] = useState(null)
    const [savedSheetsMetadata, setSavedSheetsMetadata] = useState([])
    const [selectedSheetHash, setSelectedSheetHash] = useState('')
    const [loadFrom, setLoadFrom] = useState('app')
    const [loadSheetErrorMessage, setLoadSheetErrorMessage] = useState('')

    useEffect(() => {
        let _buildSheetPaper = Snap("#build_sheet")
        if (_buildSheetPaper) {
            setBuildSheetPaper(_buildSheetPaper)
        }
        return () => {
            controller.abort();
        };

    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        changeSheetMenuOperationsOpened(sheetOperationsOpen)
    }, [sheetOperationsOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    const onSaveName = evt => {
        let value = evt.target.value
        if (value === '') {
            setSaveName('')
            return
        }
        let regexResult = value.match(/[-_)( a-zA-Z0-9]+$/)
        if ((regexResult === null) || (regexResult && regexResult.index > 0)) {
            return
        }
        setSaveName(value)
    }

    const saveSheet = () => {
        // if saving a sheet in app, dont include the custom svgs data or fonts data, cause that would
        // take too much memory. For saving as an external file, do add the data.
        if (saveOption === 'app') {
            let preparedSvg = prepareSheetSvg()
            let hash = Utility.cyrb53(preparedSvg, 'snapcounter_sheet_v3')
            if (state.savedSheets[hash]) {
                setSaveActionsResult('Sheet previously saved!')
                return
            }
            let compressedSvg = Utility.compressString(preparedSvg)

            let occupiedSlots = state.slots.filter(sl => sl.counterState !== null)
            occupiedSlots.forEach(os => {
                os.pixelsWidth = Utility.roundFloat(os.pixelsWidth, 2)
                os.centerXy.x = Utility.roundFloat(os.centerXy.x, 2)
                os.centerXy.y = Utility.roundFloat(os.centerXy.y, 2)
            })
            let saveSheetObj = {}
            saveSheetObj[hash] = {
                hash: hash,
                name: saveName,
                slots: JSON.parse(JSON.stringify(occupiedSlots)),
                sheetSettings: { ...state.sheetSettings },
                compressedSvg: compressedSvg,
                dateSaved: Utility.currentDate()
            }
            actions.savedSheets(saveSheetObj)
            setSaveActionsResult('Sheet has been saved in app')

        }
        if (saveOption === 'file') {
            // get svg of whole sheet created
            let preparedSvg = prepareSheetSvg()
            let hash = Utility.cyrb53(preparedSvg, 'snapcounter_sheet_v3')
            let compressedSvg = Utility.compressString(preparedSvg)

            // get the fonts data from the slots
            let occupiedSlots = state.slots.filter(sl => sl.counterState !== null)
            const fontsData = assembleFontsData(occupiedSlots)

            // get the custom images/svgs used
            const customSvgsData = assembleCustomSvgsData(occupiedSlots)

            const saveSheetData = {
                version: 3,
                type: 'sheet',
                hash: hash,
                name: saveName,
                slots: occupiedSlots,
                sheetSettings: { ...state.sheetSettings },
                compressedSvg: compressedSvg,
                fontsData,
                customSvgsData,
                dateSaved: Utility.currentDate()
            }
            var zip = new JSZip();
            zip.file(saveName + ".json", JSON.stringify(saveSheetData));
            zip.generateAsync({ type: "blob", compression: "DEFLATE" })
                .then(function (content) {
                    // see FileSaver.js
                    saveAs(content, saveName + '.scz');
                });

            setSaveActionsResult('Sheet has been saved to file')
        }

        if (saveOption === 'svg') {
            let occupiedSlots = state.slots.filter(sl => sl.counterState !== null)
            const fontsData = assembleFontsData(occupiedSlots)
            const preparedSvg = prepareSheetSvg(fontsData)
            const blob = new Blob([preparedSvg], { type: 'image/svg+xml' });
            saveAs(blob, saveName + '.svg');
        }

        if (saveOption === 'png') {

            const getImageDataURL = (image, type = 'png') => {
                if (type === 'jpg') type = 'jpeg';
                // const { width, height } = image;
                let width = 0
                let height = 0
                let printableWidthInches = sheetParameters.printableAreaMM[0] * 0.0393701
                let printableHeightInches = sheetParameters.printableAreaMM[1] * 0.0393701
                if (sheetParameters.height / sheetParameters.width) {
                    width = (sheetParameters.width) * ((printableWidthInches * 300) / sheetParameters.width)
                    height = (sheetParameters.height) * ((printableWidthInches * 300) / sheetParameters.width)
                }
                else {
                    width = (sheetParameters.width) * (((printableHeightInches * 300) * 300) / sheetParameters.height)
                    height = (sheetParameters.height) * ((printableHeightInches * 300) / sheetParameters.height)
                }
                const canvas = document.createElement('canvas');

                const context = canvas.getContext('2d');
                canvas.width = width;
                canvas.height = height;

                if (type === 'jpeg') {
                    context.fillStyle = 'white';
                    context.fillRect(0, 0, width, height);
                }

                context.drawImage(image, 0, 0, width, height);

                return canvas.toDataURL(`image/${type}`);
            }

            const convertSvg = (svgString, type = 'png') => new Promise((res, rej) => {
                const image = new Image();
                image.onload = () => res(getImageDataURL(image, type));
                image.src = `data:image/svg+xml,${encodeURIComponent(svgString)}`;
            });

            let occupiedSlots = state.slots.filter(sl => sl.counterState !== null)
            const fontsData = assembleFontsData(occupiedSlots)
            let source = prepareSheetSvg(fontsData)
            convertSvg(source, 'png').then(dataURL => {
                saveAs(dataURL, saveName + '.png');
            })
        }
    }

    const assembleCustomSvgsData = occupiedSlots => {
        let customSvgsData = []
        occupiedSlots.forEach(os => {
            os.counterState.package.customImageSvgKeys.forEach(iSvgKey => {
                if (!customSvgsData.find(csvgd => csvgd.svgKey === iSvgKey)) {
                    let foundSvg = state.svgs.find(ssvg => ssvg.svgKey === iSvgKey)
                    if (foundSvg) {
                        foundSvg.type = 'image'
                        customSvgsData.push(foundSvg)
                    }
                }
            })
            os.counterState.package.customSvgKeys.forEach(iSvgKey => {
                if (!customSvgsData.find(csvgd => csvgd.svgKey === iSvgKey)) {
                    let foundSvg = state.svgs.find(ssvg => ssvg.svgKey === iSvgKey)
                    if (foundSvg) {
                        foundSvg.type = 'svg'
                        customSvgsData.push(foundSvg)
                    }
                }
            })
        })

        return customSvgsData
    }

    const detectFontsInALV = (alv) => {
        let fontInputKeys = []
        let fontFamilies = []

        // find layers that have fonts in them
        state.layers.forEach(ly => {
            let fontInput = ly.inputs.find(inp => inp.named === 'font')
            if (fontInput) {
                if (!fontInputKeys.includes(fontInput.inputKey)) {
                    fontInputKeys.push(fontInput.inputKey)
                }
            }
        })
        let objectKeys = Object.keys(alv)
        objectKeys.forEach(key => {
            let splitKey = key.split('_')
            if (splitKey.length === 2) {
                let inputKey = Number(splitKey[1])
                if (fontInputKeys.includes(inputKey)) {
                    if (!fontFamilies.includes(alv[key])) {
                        fontFamilies.push(alv[key])
                    }
                }
            }
        })

        return fontFamilies
    }

    const assembleFontsData = occupiedSlots => {
        let fontsUsed = []
        let fontData = []
        occupiedSlots.forEach(os => {
            if (os.counterState.package.fonts.length > 0) {
                fontsUsed = fontsUsed.concat(os.counterState.package.fonts)
            }
        })
        if (fontsUsed.length > 0) {
            fontsUsed = [...(new Set(fontsUsed))]
        }

        fontsUsed.forEach(fontFamily => {
            let fd = state.fonts.find(st => st.fontFamily === fontFamily)
            if (fd) {
                fontData.push(fd)
            }
        })

        return fontData
    }

    const insertFontsToDef = (paper, fontsData) => {
        if (!fontsData || !Array.isArray(fontsData) || fontsData.length === 0) {
            return
        }
        let styleString = '<defs><style id="fontsStyle" type="text/css">'
        fontsData.forEach(fontData => {

            styleString += "@font-face { font-family: '" + fontData.fontFamily + "'; "
            styleString += 'font-weight: ' + fontData.fontWeight + '; '
            if (fontData.fontSrc.includes("format('woff2')") === false) {
                styleString += 'src: ' + fontData.fontSrc + " format('woff2');"
            }
            else {
                styleString += 'src: ' + fontData.fontSrc + ';'
            }
            styleString += '}'
            styleString = styleString.replaceAll(';;', ';')

        })
        styleString += '</style></defs>'
        let snapFont = Snap.parse(styleString)
        paper.append(snapFont)

        const svg = paper.toString()
        return svg
    }

    useEffect(() => {
        let arr = []
        if (state.savedSheets) {
            for (const [key, value] of Object.entries(state.savedSheets)) {
                arr.push({ hash: key, name: value.name })
            }
            setSavedSheetsMetadata(arr)
        }
    }, [state.savedSheets])

    useEffect(() => {
        setPrintableAreaMM_width(sheetParameters.printableAreaMM[0])
        setPrintableAreaMM_height(sheetParameters.printableAreaMM[1])
    }, [sheetParameters])

    const prepareSheetSvg = (fontsData = []) => {
        const originalSvgSource = state.sheetPaper.toString()

        buildSheetPaper.attr({ width: sheetParameters.width, height: sheetParameters.height });
        let parsed = Snap.parse(originalSvgSource)

        if (fontsData.length > 0) {
            insertFontsToDef(buildSheetPaper, fontsData)
        }
        buildSheetPaper.append(parsed)

        let holderElements = buildSheetPaper.selectAll(`[data-type="holder"]`)
        holderElements.forEach(holder => {
            if (holder) {
                holder.remove()
            }
        })
        let holderTexts = buildSheetPaper.selectAll(`[data-type="holderText"]`);
        holderTexts.forEach(holderText => {
            if (holderText) {
                holderText.remove()
            }
        })
        let pageMargins = buildSheetPaper.selectAll(`[data-type="pageMargin"]`);
        pageMargins.forEach(pageMargin => {
            if (pageMargin) {
                pageMargin.remove()
            }
        })

        let preparedSvg = buildSheetPaper.toString()

        preparedSvg = preparedSvg.replace('xmlns="http://www.w3.org/2000/svg"', 'xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"')

        preparedSvg = preparedSvg.replaceAll('<defs></defs>', '')
        preparedSvg = preparedSvg.replaceAll('style="pointer-events: visiblepainted;"', '')
        buildSheetPaper.clear()
        return preparedSvg
    }

    const saveActionsOk = () => {
        setSaveActionsResult('')
    }

    const onChangeGuideType = guideType => {
        if (sgLastSentGuideChange.value !== guideType) {
            sgLastSentGuideChange.value = guideType
            changeGuideType(guideType)
        }
    }

    const onChangePrintableArea = evt => {
        let id = evt.target.id
        let value = evt.target.value
        let index = -1
        if (id.includes('width')) {
            index = 0
        }
        if (id.includes('height')) {
            index = 1
        }
        if (index > -1) {
            if (Utility.isNumeric(value)) {
                value = Utility.roundFloat(value, 3)
                if (value >= 100 && value <= 400) {
                    let printableAreaMM = [...sheetParameters.printableAreaMM]
                    printableAreaMM[index] = value
                    changePrintableArea(printableAreaMM)
                }
            }
            if (index === 0) {
                setPrintableAreaMM_width(value)
            }
            if (index === 1) {
                setPrintableAreaMM_height(value)
            }
        }
    }

    const resetPrintableArea = () => {
        changePrintableArea([207.44, 270.94])
        setPrintableAreaMM_width(207.44)
        setPrintableAreaMM_height(270.94)
    }

    const printableAreaIsAtDefaults = () => {
        return printableAreaMM_width === 207.44 && printableAreaMM_height === 270.94
    }

    const validPrintableAreaWidth = () => {
        if (printableAreaMM_width === '') {
            return false
        }
        if (Utility.isNumeric(printableAreaMM_width)) {
            let v = Math.round(printableAreaMM_width)
            if (v < 100 || v > 500) {
                return false
            }
            return true
        }

        return false
    }

    const validPrintableAreaHeight = () => {
        if (printableAreaMM_height === '') {
            return false
        }
        if (Utility.isNumeric(printableAreaMM_height)) {
            let v = Math.round(printableAreaMM_height)
            if (v < 100 || v > 500) {
                return false
            }
            return true
        }

        return false
    }

    const loadSheetFromApp = evt => {
        let hash = evt.target.value
        if (hash) {
            let sheet = state.savedSheets[hash]
            if (sheet && sheet.sheetSettings && sheet.slots) {
                let sheetSettings = sheet.sheetSettings
                let slots = sheet.slots
                actions.loadSheet({ sheetSettings, slots })
                setSelectedSheetHash(hash)
            }
        }
    }

    const onLoadFrom = from => {
        setLoadFrom(from)
    }

    const digOutImages = (imageLayerKeys, svg) => {
        // cut off the end of the wrapper
        svg = svg.substring(0, svg.length - ('</g></svg>'.length))

        let layerPos = []
        let layerStart = svg.indexOf('<svg width="100%" height="100%" id="svgLayer')
        while (layerStart > -1) {

            layerStart = svg.indexOf('<svg width="100%" height="100%" id="svgLayer', layerStart + 1)
            let str = svg.substring(layerStart)
            let layerIdStart = str.indexOf('id="svgLayer')
            let endIdStart = str.indexOf('"', layerIdStart + 12)
            let number = Number(str.substring(layerIdStart + 12, endIdStart))
            layerPos.push({ layerKey: number, layerStart: layerStart, layerEnd: -1, endPoint: -1, image: '' })
        }
        // layerPos[layerPos.length - 1].endPoint = svg.length

        for (let i = 0; i < layerPos.length - 1; i++) {
            let pos = layerPos[i]
            let nextPos = layerPos[i + 1].layerStart
            pos.layerEnd = svg.lastIndexOf('</svg>', nextPos)
        }
        layerPos[layerPos.length - 1].endPoint = svg.length


        // get image out of the layer
        for (let i = 0; i < layerPos.length; i++) {
            let svgStart = layerPos[i].layerStart
            let imgStart = svg.indexOf('<image', svgStart)
            let imgEnd = svg.indexOf('/image>', imgStart)
            layerPos[i].image = svg.substring(imgStart, imgEnd + 7)
        }
        let extractedImages = layerPos.filter(lp => imageLayerKeys.includes(lp.layerKey))

        return extractedImages.map(ei => { return { layerKey: ei.layerKey, image: ei.image } })
    }

    const clickClearSheet = () => {
        let slots = [...state.slots]
        slots.forEach(sl => sl.counterState = null)
        actions.slots(slots)
    }

    const customSvgFixups = (data) => {
        // fix the tags issues
        data.customSvgsData.forEach(isvg => {
            let svgString = isvg.svgCode
            let xmlTagStart = svgString.indexOf('<?xml')
            if (xmlTagStart > -1) {
                let xmlTagEnd = svgString.indexOf('?>', xmlTagStart + 1)
                if ((xmlTagStart > -1 && xmlTagEnd > -1) && (xmlTagEnd > xmlTagStart)) {
                    svgString = svgString.substring(xmlTagEnd + 2)
                }
            }
            let docTypeStart = svgString.indexOf('<!DOCTYPE')
            if (docTypeStart > -1) {
                let docTypeEnd = svgString.indexOf('>', docTypeStart + 1)
                if ((docTypeStart > -1 && docTypeEnd > -1) && (docTypeEnd > docTypeStart)) {
                    svgString = svgString.substring(docTypeEnd + 1)
                }
            }
            isvg.svgCode = svgString.trim()
        })

        // create a copy of state.svgs with the prepends removed
        // get parent layer key of custom layers
        let customImageLayer = state.layers.find(ly => ly.layerName === 'custom images')
        let customSvgLayer = state.layers.find(ly => ly.layerName === 'custom svgs')
        let customImageLayerKey = customImageLayer.layerKey
        let customSvgLayerKey = customSvgLayer.layerKey
        let customImageRequiredKey = customImageLayer.layerActiveRequiredInputKey
        let customImageLayerKeysFromSlots = []
        let customSvgLayerKeysFromSlots = []
        let stateSvgsCopy = JSON.parse(JSON.stringify(state.svgs))
        stateSvgsCopy = stateSvgsCopy.filter(ss => ss.svgKey >= 3000)


        let svgNames = stateSvgsCopy.map(ss => ss.svgName)
        let incrementNumber = ''

        data.slots.forEach(slot => {
            if (slot.counterState) {
                slot.counterState.package.layers.forEach(layer => {
                    if (layer.layerKey === customImageLayerKey || layer.parentLayerKey === customImageLayerKey) {
                        if (customImageLayerKeysFromSlots.find(cilk => cilk === layer.layerKey) === undefined) {
                            customImageLayerKeysFromSlots.push(layer.layerKey)
                        }
                    }
                    if (layer.layerKey === customSvgLayerKey || layer.parentLayerKey === customSvgLayerKey) {
                        if (customSvgLayerKeysFromSlots.find(cilk => cilk === layer.layerKey) === undefined) {
                            customSvgLayerKeysFromSlots.push(layer.layerKey)
                        }
                    }
                })
            }
        })

        // go through the slots and find the ones with the svgKeys that need to be dug out
        let retrievedImageSvgs = []
        data.slots.forEach(slot => {
            if (slot.counterState) {
                let alvFront = JSON.parse(slot.counterState.package.counterSideActive.front)
                let alvRear = JSON.parse(slot.counterState.package.counterSideActive.rear)
                let layerKeyToImageSvgKey = []
                customImageLayerKeysFromSlots.forEach(layerKey => {
                    let detectedSvgKey = alvFront[layerKey + '_' + customImageRequiredKey]
                    if (!detectedSvgKey) {
                        detectedSvgKey = alvRear[layerKey + '_' + customImageRequiredKey]
                    }
                    if (detectedSvgKey) {
                        layerKeyToImageSvgKey.push({ layerKey, svgKey: detectedSvgKey })
                    }
                })
                let dugoutImages = digOutImages(customImageLayerKeysFromSlots, slot.counterState.frontSvg)
                let dugoutImagesRear = digOutImages(customImageLayerKeysFromSlots, slot.counterState.rearSvg)
                dugoutImagesRear.forEach(dige => {
                    if (dugoutImages.find(dig => dig.imageSvgKey === dige.imageSvgKey) === undefined) {
                        dugoutImages.push(dige)
                    }
                })
                dugoutImages.forEach(dig => {
                    let found = layerKeyToImageSvgKey.find(lk => lk.layerKey === dig.layerKey)
                    if (found) {
                        dig.imageSvgKey = found.svgKey
                    }
                })
                dugoutImages.forEach(dig => {
                    if (retrievedImageSvgs.find(ris => ris.imageSvgKey === dig.imageSvgKey) === undefined) {
                        retrievedImageSvgs.push({ imageSvgKey: dig.imageSvgKey, image: dig.image })
                    }
                })
            }
        })

        // got the found image svg keys into slot.counterState.package.customImageSvgKeys
        data.slots.forEach(slot => {
            if (slot.counterState) {
                // since we have the real image svg keys, just replace them with whatever is in the incoming slot.
                slot.counterState.package.customImageSvgKeys = retrievedImageSvgs.map(ris => ris.imageSvgKey)
            }
        })

        retrievedImageSvgs.forEach(ris => {
            let svgName = 'imported image'
            let tryName = svgName
            let found = svgNames.find(sn => sn === svgName)
            while (found) {
                if (incrementNumber === '') {
                    incrementNumber = 2
                }
                else {
                    incrementNumber++
                }
                tryName = svgName + incrementNumber
                found = svgNames.find(sn => sn === tryName) // eslint-disable-line no-loop-func
            }
            svgName = tryName
            svgNames.push(svgName)

            if (data.customSvgsData.find(csd => csd.svgKey === ris.imageSvgKey) === undefined) {
                data.customSvgsData.push({
                    type: 'image',
                    svgKey: ris.imageSvgKey,
                    svgName: svgName,
                    svgCode: ris.image,
                    uniquePrepend: ''
                })
            }
        })

        return data
    }

    const loadSheetFromFile = evt => {
        let file = evt.target.files[0]
        JSZip.loadAsync(file)
            .then((zip) => {
                Object.keys(zip.files).forEach((filename) => {
                    zip.files[filename].async('string').then(async (fileData) => {
                        let data = null
                        data = JSON.parse(fileData)
                        //console.log('data:', JSON.parse(JSON.stringify(data)))

                        // get the state images and svgs lists
                        let stateImagesLayer = state.layers.find(sl => sl.layerName === 'custom images')
                        let stateSvgsLayer = state.layers.find(sl => sl.layerName === 'custom svgs')

                        if (!stateImagesLayer || !stateSvgsLayer) {
                            console.error('cannot find custom layers in state.')
                            return
                        }

                        if (data.version === 3) {
                            if (data.type !== 'sheet') {
                                actions.addErrorMessages([{
                                    comment: "File error",
                                    lineItems: ['this file is a counter file, not a sheet file']
                                }])
                            }
                            else {
                                // version 3 sheet

                                // get the current state lists of custom images and custom svgs.
                                let customImagesLayer = state.layers.find(ly => ly.layerName === 'custom images');
                                let customSvgsLayer = state.layers.find(ly => ly.layerName === 'custom svgs');
                                let imagesRequiredInputKey = customImagesLayer.layerActiveRequiredInputKey;
                                let svgsRequiredInputKey = customSvgsLayer.layerActiveRequiredInputKey;
                                let customImagesList = []
                                let customSvgsList = []
                                let input = customImagesLayer.inputs.find(li => li.named === 'svgKey')
                                if (input) {
                                    customImagesList = [...input.list]
                                }
                                input = customSvgsLayer.inputs.find(li => li.named === 'svgKey')
                                if (input) {
                                    customSvgsList = [...input.list]
                                }


                                let changeCustomSvgKeysFromTo = []

                                data = customSvgFixups(data) // changes and adds in tags to incoming svgs so they have the right svg display parameters.

                                // check for incoming fonts
                                let fontsData = data.fontsData
                                let installFonts = []
                                fontsData.forEach(fd => {
                                    if (!state.fonts.find(sf => sf.fontFamily === fd.fontFamily)) {
                                        installFonts.push(fd)
                                    }
                                })
                                if (installFonts.length > 0) {
                                    let fonts = [...state.fonts, ...installFonts]
                                    actions.fonts(fonts)
                                }

                                // create a cleaned version of state.svgs for matching purposes.
                                let stateSvgsCopy = JSON.parse(JSON.stringify(state.svgs))
                                let stateSvgsStripped = stateSvgsCopy.map(ss => {
                                    ss.svgCode = ss.svgCode.replace(/\s+/g, "");
                                    if (ss.uniquePrepend) {
                                        ss.svgCode = ss.svgCode.replaceAll(ss.uniquePrepend, '')
                                    }
                                    return ss
                                })

                                // the data.slots.counterState.package.layers seems to sometimes be incorrect.
                                // A more reliable method is to go through each slot's alv keys.
                                const identifyLayersByAlv = (alv, layers, topLayerKeys) => {
                                    let iLayers = []
                                    for (const [key] of Object.entries(alv)) {
                                        let layerKey = Number(key.split('_')[0])
                                        let inputKey = Number(key.split('_')[1])
                                        let found = iLayers.find(il => il.layerKey === layerKey)
                                        if( !found ) {
                                            found = state.layers.find( sl => sl.layerKey === layerKey )
                                        }
                                        if (!found) {
                                            let inputs = []
                                            inputs.push(inputKey)
                                            iLayers.push({ layerKey, inputs })
                                        }
                                        else {
                                            found.inputs.push(inputKey)
                                        }
                                    }
 
                                    state.layers.forEach(sl => {
                                        let inputKeysFromLayer = sl.layerInputKeys
                                        iLayers.forEach(iLayer => {
                                          //  console.log('xxx inputKeysFromLayer:', inputKeysFromLayer)
                                          //  console.log('xxx iLayer.inputs:', iLayer.inputs)

                                            let match = Utility.compareArrays(inputKeysFromLayer, iLayer.inputs)
                                            // special check for line layer, since I changed the opacity input from 55 to 112
                                            if( !match && iLayer.inputs.includes(107) && inputKeysFromLayer.includes(107) ) {
                                                match = true
                                                //inputKeysFromLayer = inputKeysFromLayer.filter( ik => ik !== 55)
                                                
                                            }
                                         //   console.log('match result is:', match)
                                            if (match) {
                                                if( !topLayerKeys.includes(iLayer.layerKey) ) {
                                                    iLayer.layer = JSON.parse(JSON.stringify(sl))
                                                    iLayer.layer.parentLayerKey = sl.layerKey
                                                    iLayer.layer.layerKey = iLayer.layerKey
                                                    iLayer.layer.layerActive = 0
                                                    iLayer.layer.layerHidden = 0
                                                    // try to find layerOrder for this dupe layer from incoming layers.
                                                    let found = layers.find(ly => ly.layerKey === iLayer.layerKey)
                                                    if (found) {
                                                        iLayer.layer.layerOrder = found.layerOrder + 0.5
                                                    }
                                                    else {
                                                        iLayer.layer.layerOrder = 999
                                                    }
                                                    iLayer.layer.assignedToExistingLayerKey = -1
                                                    // try to find name user had given to this dupe
                                                    found = layers.find(ly => ly.layerKey === iLayer.layerKey)
                                                    if (found) {
                                                        iLayer.layer.layerName = found.layerName
                                                    }
                                                }
                                                else {
                                                    iLayer.layer = JSON.parse(JSON.stringify(sl))
                                                    iLayer.layer.parentLayerKey = -1
                                                    iLayer.layer.layerKey = iLayer.layerKey
                                                    iLayer.layer.layerOrder = iLayer.layerOrder
                                                    iLayer.layer.assignedToExistingLayerKey = -1
                                                }
                                            }
                                        })
                                    })

                                    return iLayers
                                }



                                let dupeLayersFront = []
                                let dupeLayersRear = []
                                // get layer keys into array that are standard top level layers
                                let topLayers = state.layers.filter(sl => sl.parentLayerKey === -1)
                                let topLayerKeys = topLayers.map(sl => sl.layerKey)



                                data.slots.forEach(slot => {
                                    if (slot.counterState) {
                                        let frontAlv = JSON.parse(slot.counterState.package.counterSideActive.front)
                                        let rearAlv = JSON.parse(slot.counterState.package.counterSideActive.rear)
                                        let identifiedLayersFromAlvFront = identifyLayersByAlv(frontAlv, slot.counterState.package.layers, topLayerKeys)
                                        let identifiedLayersFromAlvRear = identifyLayersByAlv(rearAlv, slot.counterState.package.layers, topLayerKeys)

                                        identifiedLayersFromAlvFront.forEach(ilf => {
                                            if (ilf.layer.parentLayerKey > -1) {
                                                if (!dupeLayersFront.find(dlf => dlf.layerKey === ilf.layer.layerKey)) {
                                                    dupeLayersFront.push(ilf.layer)
                                                }
                                            }
                                        })
                                        identifiedLayersFromAlvRear.forEach(ilf => {
                                            if (ilf.layer.parentLayerKey > -1) {
                                                if (!dupeLayersRear.find(dlr => dlr.layerKey === ilf.layer.layerKey)) {
                                                    dupeLayersRear.push(ilf.layer)
                                                }
                                            }
                                        })

                                    }
                                })

                                let collectedDupeLayers = dupeLayersFront
                                dupeLayersRear.forEach(dlr => {
                                    if (!collectedDupeLayers.find(cdl => cdl.layerKey === dlr.layerKey)) {
                                        collectedDupeLayers.push(dlr)
                                    }
                                })

                               collectedDupeLayers.forEach( cdl => {
                                   let parentLayer = state.layers.find( sl => sl.layerKey === cdl.parentLayerKey )
                                   while( parentLayer.parentLayerKey > -1 ) {
                                       parentLayer = state.layers.find( sl => sl.layerKey === parentLayer.parentLayerKey ) // eslint-disable-line no-loop-func
                                    }
                                   if( parentLayer ) {
                                       cdl.layerType = parentLayer.layerName
                                   }
                               })


                                let availableDupeLayers = state.layers.filter(sl => sl.parentLayerKey > -1)
                                
                                availableDupeLayers = availableDupeLayers.map(sl => {
                                    return {
                                        layerType: Utility.originalLayerNameLayerKey(state.layers, sl.layerKey),
                                        layerKey: sl.layerKey,
                                        parentLayerKey: sl.parentLayerKey,
                                        assigned: false,
                                        layer: JSON.parse(JSON.stringify(sl))
                                    }
                                })

                                collectedDupeLayers.forEach(cdl => {
                                    //console.log('looking for a match for', cdl)
                                    let found = availableDupeLayers.find(adl => adl.layerType === cdl.layerType && adl.assigned === false)
                                    if (found) {
                                        //  console.log('found match for incoming dupe:', cdl, 'with avaialable dupe:', found)
                                        found.assigned = true
                                        cdl.assignedToExistingLayerKey = found.layerKey
                                    }
                                })

                                // so for the collectedDupeLayers that have matched up, we put this into a from to array.
                                // we will need to apply the changes to the activeLayerValues in the slots, and also
                                // to the layers in the slots.
                                // For those that didnt get matched up, we need to put them into a create layers array.
                                let changeLayerKeys = []
                                collectedDupeLayers.forEach(cdl => {
                                    if (cdl.assignedToExistingLayerKey > -1) {
                                        if (cdl.layerKey !== cdl.assignedToExistingLayerKey) {
                                            changeLayerKeys.push({ from: cdl.layerKey, to: cdl.assignedToExistingLayerKey, type: cdl.layerType })
                                        }
                                    }
                                })
                                // all collectedDupeLayers with an assignedToExistingLayerKey of -1 will need to be created.
                                let newLayersToCreate = []
                                let nextLayerKey2 = Math.max(...state.layers.map(sl => sl.layerKey)) + 1
                                collectedDupeLayers.forEach(cdl => {
                                    if (cdl.assignedToExistingLayerKey === -1) {
                                        let newLayer = JSON.parse(JSON.stringify(cdl))
                                        if (cdl.layerKey !== nextLayerKey2) {
                                            changeLayerKeys.push({ from: cdl.layerKey, to: nextLayerKey2, type: cdl.layerType })
                                        }
                                        newLayer.layerKey = nextLayerKey2++
                                        newLayer.layerOrder = cdl.layerOrder + 0.5 // newly created layers should be bumped up a bit from the original layer order.
                                        newLayersToCreate.push(newLayer)
                                    }
                                })

                                // make sure the layerNames in the newLayersToCreate don't conflict with existing layerNames. Probably not, but it could happen.
                                let existingLayerNames = state.layers.map(sl => sl.layerName.toLowerCase())
                                newLayersToCreate.forEach(nl => {
                                    // make sure we're assigning the parentLayerKey to the top parent, not another dupe.
                                    if( !topLayerKeys.includes(nl.parentLayerKey) ) {
                                       let topLayer = state.layers.find( sl => sl.layerName === nl.layerType )
                                       if( topLayer ) {
                                            nl.parentLayerKey = topLayer.layerKey
                                       }
                                    }
                                    let baseName = nl.layerName
                                    if (Utility.endsWithNumber(baseName)) {
                                        baseName = baseName.substring(0, baseName.length - 1)
                                    }
                                    let currentLayerName = baseName
                                    let incrementNumber = 2
                                    while (existingLayerNames.find(eln => eln === currentLayerName.toLowerCase())) { // eslint-disable-line no-loop-func
                                        currentLayerName = baseName + String(incrementNumber)
                                        incrementNumber++
                                    }
                                    nl.layerName = currentLayerName
                                    existingLayerNames.push(currentLayerName.toLowerCase())
                                })

                                // now we need to go through the layers in the slots, and change any layerKeys that are in the changeLayerKeys array.
                                data.slots.forEach(slot => {
                                    if (slot.counterState && slot.counterState.package) {
                                        slot.counterState.package.layers.forEach(layer => {
                                            let found = changeLayerKeys.find(clk => clk.from === layer.layerKey)
                                            if (found) {
                                                layer.layerKeyNew = found.to
                                            }
                                        })
                                    }
                                })

                                // do the actual change now
                                data.slots.forEach(slot => {
                                    if (slot.counterState && slot.counterState.package) {
                                        slot.counterState.package.layers.forEach(layer => {
                                            if (layer.layerKeyNew) {
                                                layer.layerKey = layer.layerKeyNew
                                                delete layer.layerKeyNew
                                            }
                                        })
                                    }
                                })

                                // now lets go through the activeLayerValues in the slots, and change any layerKeys that are in the changeLayerKeys array.
                                data.slots.forEach(slot => {
                                    if (slot.counterState && slot.counterState.package) {
                                        let newFrontAlv = JSON.parse(slot.counterState.package.counterSideActive.front)
                                        let newRearAlv = JSON.parse(slot.counterState.package.counterSideActive.rear)

                                        newFrontAlv = processLayerKeyChanges(changeLayerKeys, newFrontAlv)
                                        newRearAlv = processLayerKeyChanges(changeLayerKeys, newRearAlv)
                                        slot.counterState.package.counterSideActive.front = JSON.stringify(newFrontAlv)
                                        slot.counterState.package.counterSideActive.rear = JSON.stringify(newRearAlv)
                                    }
                                })
                                // ok look into this processLayerKeyChanges and what its returning. I think its null.
                                // we gotta switch the svgKeys in the alvs too!

                                // check for unknown incoming custom svgs
                                let customSvgsData = data.customSvgsData

                                let installSvgs = []
                                // before putting into installSvgs, make sure we are only going to install svgs that are not already in state.
                                // Go through each svg in customSvgsData, and check:
                                // Does a svg with same name and svgKey exist in state? If so, its safe to assume its the same svg. No need to do anything with it.

                                let nextSvgKey2 = Math.max(...state.svgs.map(sl => sl.svgKey)) + 1
                                if (nextSvgKey2 < 3000) {
                                    nextSvgKey2 = 3000
                                }

                                customSvgsData.forEach(isvg => {
                                    if (state.svgs.find(ss => ss.svgKey === isvg.svgKey && ss.svgName === isvg.svgName)) {
                                        ; //found with same key and name, nothing to do.
                                    }
                                    else {
                                        // else if we have a match on name but not svgKey, then record the change in svgKeys (the changes will be dealt with later)
                                        let found = state.svgs.find(ss => ss.svgName === isvg.svgName)
                                        if (found) {
                                            changeCustomSvgKeysFromTo.push({ from: isvg.svgKey, to: found.svgKey })
                                        }
                                        else {
                                            // ok no match on name. Check the svgCodes to see if there a matching svg in state.
                                            // see if the svgCode exists, even though the name is different
                                            // stateSvgsStripped had spaces removed and uniquePrepend removed.
                                            let strippedSvgCode = isvg.svgCode
                                            strippedSvgCode = strippedSvgCode.replace(/\s+/g, "");
                                            if (isvg.uniquePrepend) {
                                                strippedSvgCode = strippedSvgCode.replaceAll(isvg.uniquePrepend, '')
                                            }
                                            //console.log('checking based on svgCode equivalency')
                                            let foundInState = stateSvgsStripped.find(ss => ss.svgCode === strippedSvgCode)
                                            if (foundInState) {
                                                // we found a code match. So all we need to do is check the svgKeys to see if they are different.
                                                if (foundInState.svgKey !== isvg.svgKey) {
                                                    // and if so we'll save that info so we can change the data further down.
                                                    if (isvg.svgKey !== foundInState.svgKey) {
                                                        changeCustomSvgKeysFromTo.push({ from: isvg.svgKey, to: foundInState.svgKey, type: isvg.type })
                                                    }
                                                }
                                            }
                                            else {
                                                // else we have no match on name and no match on code, so we need to install it as a new svg.
                                                changeCustomSvgKeysFromTo.push({ from: isvg.svgKey, to: nextSvgKey2, type: isvg.type })
                                                isvg.svgKey = nextSvgKey2++
                                                installSvgs.push(isvg)
                                                if (isvg.type === 'image') {
                                                    if (customImagesList.includes(isvg.svgKey) === false) {
                                                        customImagesList.push(isvg.svgKey)
                                                    }
                                                }
                                                if (isvg.type === 'svg') {
                                                    if (customSvgsList.includes(isvg.svgKey) === false) {
                                                        customSvgsList.push(isvg.svgKey)
                                                    }
                                                }
                                            }
                                        }
                                    }
                                })

                                installSvgs.forEach(isvg => {
                                    delete isvg.type
                                })

                                // incorporate any custom svgKey changes.
                                data.slots.forEach(slot => {
                                    if (slot.counterState && slot.counterState.package) {
                                        let newFrontAlv = JSON.parse(slot.counterState.package.counterSideActive.front)
                                        let newRearAlv = JSON.parse(slot.counterState.package.counterSideActive.rear)
                                        newFrontAlv = processSvgKeyChanges(slot.number, imagesRequiredInputKey, svgsRequiredInputKey, changeCustomSvgKeysFromTo, newFrontAlv)
                                        newRearAlv = processSvgKeyChanges(slot.number, imagesRequiredInputKey, svgsRequiredInputKey, changeCustomSvgKeysFromTo, newRearAlv)
                                        slot.counterState.package.counterSideActive.front = JSON.stringify(newFrontAlv)
                                        slot.counterState.package.counterSideActive.rear = JSON.stringify(newRearAlv)
                                    }
                                })

                                // update all the custom images and svgs lists in the state layers and newLayersToCreate
                                let combinedLayers = [...state.layers, ...newLayersToCreate]
                                combinedLayers.forEach(cl => {
                                    if (cl.layerKey === customImagesLayer.layerKey || cl.parentLayerKey === customImagesLayer.layerKey) {
                                        let input = cl.inputs.find(li => li.named === 'svgKey')
                                        if (input) {
                                            input.list = [...customImagesList]
                                            if (input.list.length > 0) {
                                                cl.layerHidden = 0
                                            }
                                        }
                                    }
                                    if (cl.layerKey === customSvgsLayer.layerKey || cl.parentLayerKey === customSvgsLayer.layerKey) {
                                        let input = cl.inputs.find(li => li.named === 'svgKey')
                                        if (input) {
                                            input.list = [...customSvgsList]
                                            if (input.list.length > 0) {
                                                cl.layerHidden = 0
                                            }
                                        }
                                    }
                                })

                                // remove temporary-use keys
                                combinedLayers.forEach(cl => {
                                    delete cl.assignedToExistingLayerKey
                                    delete cl.layerType
                                })

                                // fix the individual slot svgKey callouts (used to prevent deletion of svgs if used in slots)
                                data.slots.forEach(slot => {
                                    if (slot.counterState && slot.counterState.package) {
                                        let imageKeysIncluded = []
                                        let svgKeysIncluded = []
                                        //console.log('previous imagesvgkeys:', slot.counterState.package.customImageSvgKeys)
                                        // i think this maybe removing keys that need to be in there.
                                        // just find the image and svg keys that are present in the alv front and rears, and collect from there.
                                        let alvFront = JSON.parse(slot.counterState.package.counterSideActive.front)
                                        let alvRear = JSON.parse(slot.counterState.package.counterSideActive.rear)
                                        for (const [key, value] of Object.entries(alvFront)) {
                                            let inputKey = Number(key.split('_')[1])
                                            if (inputKey === imagesRequiredInputKey) {
                                                if (value && imageKeysIncluded.includes(value) === false) {
                                                    imageKeysIncluded.push(value)
                                                }
                                            }
                                            if (inputKey === svgsRequiredInputKey) {
                                                if (value && svgKeysIncluded.includes(value) === false) {
                                                    svgKeysIncluded.push(value)
                                                }
                                            }
                                        }
                                        for (const [key, value] of Object.entries(alvRear)) {
                                            let inputKey = Number(key.split('_')[1])
                                            if (inputKey === imagesRequiredInputKey) {
                                                if (value && imageKeysIncluded.includes(value) === false) {
                                                    imageKeysIncluded.push(value)
                                                }
                                            }
                                            if (inputKey === svgsRequiredInputKey) {
                                                if (value && svgKeysIncluded.includes(value) === false) {
                                                    svgKeysIncluded.push(value)
                                                }
                                            }
                                        }
                                        slot.counterState.package.customImageSvgKeys = imageKeysIncluded
                                        slot.counterState.package.customSvgKeys = svgKeysIncluded
                                    }
                                })

                                // reorder and reindex
                                combinedLayers.sort((a, b) => a.layerOrder - b.layerOrder)
                                combinedLayers.forEach((ly, index) => {
                                    ly.layerOrder = index
                                })

                                // reset the layers in the slot.package.layers with updated layers
                                let newCustomImagesLayer = combinedLayers.find(ly => ly.layerName === 'custom images');
                                let newCustomSvgsLayer = combinedLayers.find(ly => ly.layerName === 'custom svgs');
                                let newImagesInput = newCustomImagesLayer.inputs.find(li => li.named === 'svgKey');
                                let newSvgsInput = newCustomSvgsLayer.inputs.find(li => li.named === 'svgKey');
                                data.slots.forEach(slot => {
                                    if (slot.counterState && slot.counterState.package) {
                                        slot.counterState.package.layers.forEach(layer => {
                                            let found = changeLayerKeys.find( clk => clk.from === layer.key )
                                            if( found ) {
                                                layer.layerKey = found.to
                                            }
                                            if( Utility.originalLayerName(combinedLayers, layer) === 'custom images' ) {
                                                let input = layer.inputs.find(li => li.named === 'svgKey')
                                                if( input ) {
                                                    input.list = [...newImagesInput.list]
                                                }
                                            }
                                            if( Utility.originalLayerName(combinedLayers, layer) === 'custom svgs' ) {
                                                let input = layer.inputs.find(li => li.named === 'svgKey')
                                                if( input ) {
                                                    input.list = [...newSvgsInput.list]
                                                }
                                            }
                                        })
                                    }
                                })

                                // check some slot info
                                // let slot = data.slots[5]
                                // if( slot.counterState && slot.counterState.package ) {
                                //     let alvFront = JSON.parse(slot.counterState.package.counterSideActive.front)
                                //     let alvRear = JSON.parse(slot.counterState.package.counterSideActive.front)
                                //     console.log('alvFront slot 5:', alvFront)
                                //     console.log('alvRear slot 5:', alvRear)
                                // }

                                actions.layers(combinedLayers)
                                actions.svgs([...state.svgs, ...installSvgs])

                                actions.loadSheet({
                                    sheetSettings: data.sheetSettings,
                                    slots: data.slots
                                })

                            }

                        }
                        /*  end newer version */
                        else {
                            if (data.type === 'counter') {
                                actions.addErrorMessages([{
                                    comment: "File error",
                                    lineItems: ['this file is a counter file, not a sheet']
                                }])

                            }
                            else {
                                if (data.sheetSettings && data.slots && data.type === 'sheet') {
                                    let updated = handleIncomingOldVersionSheet(data)
                                    delete updated.compressedSvg
                                    delete updated.hash
                                    actions.loadSheet({
                                        sheetSettings: data.sheetSettings,
                                        slots: updated.slots
                                    })

                                }
                                else {
                                    actions.addErrorMessages([{
                                        comment: "File error",
                                        lineItems: ['this file is a not a sheet file']
                                    }])
                                }
                            }
                        }
                    })
                })
            })

    }

    const processLayerKeyChanges = (changeLayerKeys, alv) => {
        for (const [key, value] of Object.entries(alv)) {
            changeLayerKeys.forEach(fromTo => {
                if (key.startsWith(fromTo.from + '_')) {
                    let inputKey = key.split('_')[1]
                    alv['new_' + fromTo.to + '_' + inputKey] = value
                    delete alv[key]
                }
            })
        }
        for (const [key, value] of Object.entries(alv)) {
            if (key.startsWith('new_')) {
                let fixedKey = key.replace('new_', '')
                alv[fixedKey] = value
                delete alv[key]
            }
        }

        return alv
    }

    const processSvgKeyChanges = (slotNumber, imagesRequiredInputKey, svgsRequiredInputKey, changeSvgKeys, alv) => {
        for (const [key, value] of Object.entries(alv)) {
            let inputKey = Number(key.split('_')[1])
            if (inputKey === imagesRequiredInputKey) {
                let found = changeSvgKeys.find(csk => csk.from === Number(value))
                if (found) {
                    alv['new_' + key] = found.to
                    delete alv[key]
                }
            }
            if (inputKey === svgsRequiredInputKey) {
                let found = changeSvgKeys.find(csk => csk.from === Number(value))
                if (found) {
                    alv['new_' + key] = found.to
                    delete alv[key]
                }
            }
        }
        for (const [key, value] of Object.entries(alv)) {
            if (key.startsWith('new_')) {
                let fixedKey = key.replace('new_', '')
                alv[fixedKey] = value
                delete alv[key]
            }
        }

        return alv
    }

    const updateActiveLayerValues = (activeLayerValues, changeSvgKeys, requiredInputKey) => {
        const updatedActiveLayerValues = { ...activeLayerValues };
        for (const [key, value] of Object.entries(activeLayerValues)) {
            const splitKey = key.split('_');
            if (splitKey.length === 2) {
                const inputKey = Number(splitKey[1]);
                if (inputKey === requiredInputKey) {
                    const found = changeSvgKeys.find(fromTo => fromTo.from === value);
                    if (found) {
                        updatedActiveLayerValues[key] = found.to;
                    }
                }
            }
        }
        return updatedActiveLayerValues;
    };

    const handleIncomingOldVersionSheet = data => {
        let updatedStateLayers = JSON.parse(JSON.stringify(state.layers));
        let layerDupeTypesAvailable = Utility.layerTypesInventory(state.layers, true);
        let slots = data.slots;
        let nextLayerKey = state.layers.reduce((a, b) => Number(a.layerKey) > Number(b.layerKey) ? a : b).layerKey;
        let dupeLayersNeeded = {};
        let activeSlots = slots.filter(slot => slot.counterState !== null);

        activeSlots.forEach(slot => {
            let slotActiveLayerValues = JSON.parse(JSON.stringify(slot.counterState.activeLayerValues));
            slotActiveLayerValues = removeSpacersActiveLayerValues(slotActiveLayerValues);
            slot.originalSlotLayers = JSON.parse(JSON.stringify(slot.counterState.layers));
            let slotLayers = JSON.parse(JSON.stringify(slot.counterState.layers)).filter(lr => lr.layerActive === 1);
            slotLayers = removeSpacersLayers(slotLayers);

            let layerDupeTypesAvailableCopy = JSON.parse(JSON.stringify(layerDupeTypesAvailable));
            let incomingDupeLayersTypes = Utility.layerTypesInventory(slotLayers, true).filter(idl => idl.layerKeys.length > 0);
            let changeLayerKeys = [];

            incomingDupeLayersTypes.forEach(idl => {
                let foundTypeEntry = layerDupeTypesAvailableCopy.find(lta => lta.type === idl.type);
                if (foundTypeEntry) {
                    while (idl.layerKeys.length > 0 && foundTypeEntry.layerKeys.length > 0) {
                        let matchedToLayerKey = foundTypeEntry.layerKeys.shift();
                        let fromLayerKey = idl.layerKeys.shift();
                        changeLayerKeys.push({ from: fromLayerKey, to: matchedToLayerKey });
                    }
                }
            });

            layerDupeTypesAvailableCopy = layerDupeTypesAvailableCopy.filter(ldt => ldt.layerKeys.length > 0);
            incomingDupeLayersTypes = incomingDupeLayersTypes.filter(idl => idl.layerKeys.length > 0);
            slot.needsAssignmentLayers = incomingDupeLayersTypes;

            incomingDupeLayersTypes.forEach(dl => {
                let type = dl.type;
                let amount = dl.layerKeys.length;
                dupeLayersNeeded[type] = Math.max(dupeLayersNeeded[type] || 0, amount);
            });

            changeLayerKeys.forEach(fromTo => {
                slotLayers.forEach(idl => {
                    if (idl.layerKey === fromTo.from) {
                        idl.layerKey = 'new_' + fromTo.to;
                    }
                });
                for (const [key, value] of Object.entries(slotActiveLayerValues)) {
                    if (key.startsWith(fromTo.from + '_')) {
                        let inputKey = key.split('_')[1];
                        let newKey = 'new_' + fromTo.to + '_' + inputKey;
                        slotActiveLayerValues[newKey] = value;
                        delete slotActiveLayerValues[key];
                    }
                }
            });

            for (const [key, value] of Object.entries(slotActiveLayerValues)) {
                if (key.startsWith('new_')) {
                    let fixedKey = key.replace('new_', '');
                    slotActiveLayerValues[fixedKey] = value;
                    delete slotActiveLayerValues[key];
                }
            }

            slot.counterState.layers = slotLayers;
            slot.counterState.activeLayerValues = slotActiveLayerValues;
        });

        let createdLayers = [];
        for (const [type, amount] of Object.entries(dupeLayersNeeded)) {
            for (let i = 0; i < amount; i++) {
                nextLayerKey++;
                let allLayers = [...state.layers, ...createdLayers];
                if( type ) {
                let existingLayer = state.layers.find(sl => sl.layerName === type);
                let layerOrder = existingLayer.layerOrder + 0.5;
                let dupeLayer = createDuplicateLayer(type, nextLayerKey, layerOrder, allLayers);
                createdLayers.push(dupeLayer);
                }
            }
        }

        activeSlots.forEach(slot => {
            let changeLayerKeysToCreated = [];
            let createdLayersForSlot = JSON.parse(JSON.stringify(createdLayers));
            slot.needsAssignmentLayers.forEach(nal => {
                let neededType = nal.type;
                nal.layerKeys.forEach(fromLayerKey => {
                    let createdLayer = createdLayersForSlot.find(cl => cl.layerName.replace(/[0-9]/g, '') === neededType);
                    if (createdLayer) {
                        createdLayer = JSON.parse(JSON.stringify(createdLayer));
                        createdLayersForSlot = createdLayersForSlot.filter(clfs => clfs.layerKey !== createdLayer.layerKey);
                        let toLayerKey = createdLayer.layerKey;
                        changeLayerKeysToCreated.push({ from: fromLayerKey, to: toLayerKey });
                    }
                });
            });

            let slotActiveLayerValues = slot.counterState.activeLayerValues;
            changeLayerKeysToCreated.forEach(fromTo => {
                slot.counterState.layers.forEach(idl => {
                    if (idl.layerKey === fromTo.from) {
                        idl.layerKey = 'new_' + fromTo.to;
                    }
                });
                for (const [key, value] of Object.entries(slotActiveLayerValues)) {
                    if (key.startsWith(fromTo.from + '_')) {
                        let inputKey = key.split('_')[1];
                        let newKey = 'new_' + fromTo.to + '_' + inputKey;
                        slotActiveLayerValues[newKey] = value;
                        delete slotActiveLayerValues[key];
                    }
                }
            });

            slot.counterState.layers.forEach(idl => {
                if (typeof idl.layerKey === 'string' && idl.layerKey.startsWith('new_')) {
                    idl.layerKey = Number(idl.layerKey.replace('new_', ''));
                }
            });

            for (const [key, value] of Object.entries(slotActiveLayerValues)) {
                if (key.startsWith('new_')) {
                    let fixedKey = key.replace('new_', '');
                    slotActiveLayerValues[fixedKey] = value;
                    delete slotActiveLayerValues[key];
                }
            }

            slot.counterState.activeLayerValues = slotActiveLayerValues;
        });

        let customIncomingSvgsDetected = [];
        let customIncomingImagesDetected = [];
        let changeSvgKeys = [];
        let useKey = state.svgs.reduce((a, b) => Number(a.svgKey) > Number(b.svgKey) ? a : b).svgKey;
        useKey = useKey < 3000 ? 3000 : useKey + 1;
        let cleanedStateSvgs = state.svgs.filter(ssvg => ssvg.svgKey > useKey).map(csvg => ({
            ...csvg,
            svgCode: Utility.stripXmlTags(csvg.svgCode)
        }));

        slots.forEach(slot => {
            let slotCustomImagesLayer = slot.originalSlotLayers.find(ly => ly.layerName === 'custom images');
            let slotCustomSvgsLayer = slot.originalSlotLayers.find(ly => ly.layerName === 'custom svgs');
            let imagesInput = slotCustomImagesLayer.inputs.find(li => li.named === 'svgKey');
            let svgsInput = slotCustomSvgsLayer.inputs.find(li => li.named === 'svgKey');
            let incomingSvgs = slot.counterState.customSvgs;

            incomingSvgs.forEach(svg => {
                if (imagesInput.list.includes(svg.svgKey) && !customIncomingImagesDetected.find(imagesSvg => imagesSvg.svgKey === svg.svgKey)) {
                    customIncomingImagesDetected.push(svg);
                }
                if (svgsInput.list.includes(svg.svgKey) && !customIncomingSvgsDetected.find(svgsSvg => svgsSvg.svgKey === svg.svgKey)) {
                    customIncomingSvgsDetected.push(svg);
                }
            });
        });

        let deleteList = [];
        cleanedStateSvgs.forEach(cssvg => {
            customIncomingImagesDetected.forEach((incomingImageSvg, c) => {
                let stateCheck = Utility.stripXmlTags(cssvg.svgCode.replaceAll(cssvg.uniquePrepend, '')).replace(/[\r\n]+/gm, " ");
                let incomingCheck = Utility.stripXmlTags(incomingImageSvg.svgCode.replaceAll(incomingImageSvg.uniquePrepend, '')).replace(/[\r\n]+/gm, " ");
                let rawQ = stringComparison.cosine.similarity(stateCheck, incomingCheck);
                if (rawQ > 0.95 && (Utility.extractNativeId(incomingCheck) === Utility.extractNativeId(stateCheck) || incomingImageSvg.svgName === cssvg.svgName)) {
                    if (cssvg.svgKey !== incomingImageSvg.svgKey) {
                        changeSvgKeys.push({ from: incomingImageSvg.svgKey, to: cssvg.svgKey, type: 'image' });
                    }
                    deleteList.push(incomingImageSvg.svgKey);
                }
            });
            customIncomingImagesDetected = customIncomingImagesDetected.filter(iid => !deleteList.includes(iid.svgKey));

            customIncomingSvgsDetected.forEach((incomingSvg, c) => {
                let stateCheck = Utility.stripXmlTags(cssvg.svgCode.replaceAll(cssvg.uniquePrepend, '')).replace(/[\r\n]+/gm, " ");
                let incomingCheck = Utility.stripXmlTags(incomingSvg.svgCode.replaceAll(incomingSvg.uniquePrepend, '')).replace(/[\r\n]+/gm, " ");
                let rawQ = stringComparison.cosine.similarity(stateCheck, incomingCheck);
                if (rawQ > 0.95 && (Utility.extractNativeId(incomingCheck) === Utility.extractNativeId(stateCheck) || incomingSvg.svgName === cssvg.svgName)) {
                    if (cssvg.svgKey !== incomingSvg.svgKey) {
                        changeSvgKeys.push({ from: incomingSvg.svgKey, to: cssvg.svgKey, type: 'svg' });
                    }
                    deleteList.push(incomingSvg.svgKey);
                }
            });
            customIncomingSvgsDetected = customIncomingSvgsDetected.filter(iid => !deleteList.includes(iid.svgKey));
        });

        let customImagesLayer = state.layers.find(ly => ly.layerName === 'custom images');
        let customSvgsLayer = state.layers.find(ly => ly.layerName === 'custom svgs');
        let imagesRequiredInputKey = customImagesLayer.layerActiveRequiredInputKey;
        let svgsRequiredInputKey = customSvgsLayer.layerActiveRequiredInputKey;

        activeSlots.forEach(slot => {
            slot.counterState.activeLayerValues = updateActiveLayerValues(slot.counterState.activeLayerValues, changeSvgKeys, imagesRequiredInputKey);
            slot.counterState.activeLayerValues = updateActiveLayerValues(slot.counterState.activeLayerValues, changeSvgKeys, svgsRequiredInputKey);
        });

        let nextSvgKey = Math.max(...state.svgs.map(svg => parseInt(svg.svgKey)), 0);
        let importSvgs = [];
        changeSvgKeys = [];
        customIncomingImagesDetected.forEach(svg => {
            let xlinkStart = svg.svgCode.indexOf('xlink:href');
            if (xlinkStart > -1) {
                let xlinkEnd = svg.svgCode.indexOf('></image></svg>');
                if (xlinkEnd > xlinkStart) {
                    let newImageSrc = svg.svgCode.substring(xlinkStart, xlinkEnd);
                    newImageSrc = '<image width="100%" height="100%" ' + newImageSrc + '/>';
                    svg.svgCode = newImageSrc;
                }
            }
            nextSvgKey++;
            changeSvgKeys.push({ from: svg.svgKey, to: nextSvgKey, type: 'image' });
            svg.svgKey = nextSvgKey;
            importSvgs.push(svg);
        });

        customIncomingSvgsDetected.forEach(svg => {
            svg.svgCode = svg.svgCode.replace(/\bwidth="(\d+(\.\d*)?|\.\d+)"/, 'width="100%"');
            svg.svgCode = svg.svgCode.replace(/\bheight="(\d+(\.\d*)?|\.\d+)"/, 'height="100%"');
            nextSvgKey++;
            changeSvgKeys.push({ from: svg.svgKey, to: nextSvgKey, type: 'svg' });
            svg.svgKey = nextSvgKey;
            importSvgs.push(svg);
        });

        activeSlots.forEach(slot => {
            slot.counterState.activeLayerValues = updateActiveLayerValues(slot.counterState.activeLayerValues, changeSvgKeys, imagesRequiredInputKey);
            slot.counterState.activeLayerValues = updateActiveLayerValues(slot.counterState.activeLayerValues, changeSvgKeys, svgsRequiredInputKey);
        });

        let stateCustomImagesLayer = updatedStateLayers.find(ly => ly.layerName === 'custom images');
        let stateCustomSvgsLayer = updatedStateLayers.find(ly => ly.layerName === 'custom svgs');
        let imagesList = [];
        let svgsList = [];
        if (stateCustomImagesLayer) {
            let input = stateCustomImagesLayer.inputs.find(li => li.named === 'svgKey');
            if (input && input.list && Array.isArray(input.list)) {
                changeSvgKeys.forEach(fromTo => {
                    if (fromTo.type === 'image' && !input.list.includes(fromTo.to)) {
                        input.list.push(fromTo.to);
                    }
                });
                imagesList = [...input.list];
            }
        }
        if (stateCustomSvgsLayer) {
            let input = stateCustomSvgsLayer.inputs.find(li => li.named === 'svgKey');
            if (input && input.list && Array.isArray(input.list)) {
                changeSvgKeys.forEach(fromTo => {
                    if (fromTo.type === 'svg' && !input.list.includes(fromTo.to)) {
                        input.list.push(fromTo.to);
                    }
                });
                svgsList = [...input.list];
            }
        }

        let customImagesLayers = Utility.getDupeCustomImagesLayers(updatedStateLayers);
        let customSvgsLayers = Utility.getDupeCustomSvgsLayers(updatedStateLayers);
        if (imagesList.length > 0) {
            customImagesLayers.forEach(cil => {
                let input = cil.inputs.find(li => li.named === 'svgKey');
                if (input && input.list) {
                    input.list = imagesList;
                }
            });
            let imagesLayer = updatedStateLayers.find(usl => usl.layerName === 'custom images');
            if (imagesLayer) {
                imagesLayer.layerHidden = 0;
            }
        }
        if (svgsList.length > 0) {
            customSvgsLayers.forEach(csl => {
                let input = csl.inputs.find(li => li.named === 'svgKey');
                if (input && input.list) {
                    input.list = svgsList;
                }
            });
            let svgsLayer = updatedStateLayers.find(usl => usl.layerName === 'custom svgs');
            if (svgsLayer) {
                svgsLayer.layerHidden = 0;
            }
        }
        if (importSvgs.length > 0) {
            actions.svgs([...state.svgs, ...importSvgs]);
        }

        data.slots.forEach(slot => {
            let layerSvgs = slot.counterState.layers.map(ly => ly.svg);
            slot.counterState.combinedSvg = combineDirectSvgs(layerSvgs);
            slot.counterState.layers.forEach(ly => delete ly.svg);
        });

        actions.layers([...updatedStateLayers, ...createdLayers]);

        data.slots.forEach(slot => {
            let customImageSvgKeys = [];
            let customSvgKeys = [];
            for (const [key, value] of Object.entries(slot.counterState.activeLayerValues)) {
                let splitKey = key.split('_');
                if (Number(splitKey[1]) === imagesRequiredInputKey && !customImageSvgKeys.includes(value)) {
                    customImageSvgKeys.push(value);
                }
                if (Number(splitKey[1]) === svgsRequiredInputKey && !customSvgKeys.includes(value)) {
                    customSvgKeys.push(value);
                }
            }

            let fonts = detectFontsInALV(slot.counterState.activeLayerValues);

            slot.counterState = {
                frontSvg: slot.counterState.combinedSvg,
                rearSvg: slot.counterState.combinedSvg,
                package: {
                    counterSideActive: {
                        active: "front",
                        front: JSON.stringify(slot.counterState.activeLayerValues),
                        rear: JSON.stringify(slot.counterState.activeLayerValues)
                    },
                    customImageSvgKeys: customImageSvgKeys,
                    customSvgKeys: customSvgKeys,
                    fonts: fonts,
                    layers: slot.counterState.layers,
                    layersEffects: []
                }
            };
            slot.rearNumber = 0;
            slot.rearPosition = { col: 0, row: 0 };
            delete slot.svg;
            delete slot.needsAssignmentLayers;
            delete slot.updatedSvg;
            delete slot.originalSlotLayers;
        });

        let newSheetSettings = data.sheetSettings;
        newSheetSettings.sheetSide = 'front';
        let updated = {
            compressedSvg: null,
            customSvgsData: [],
            dateSaved: data.dateSaved,
            fontsData: [],
            hash: null,
            name: data.name,
            sheetSettings: newSheetSettings,
            slots: data.slots,
            type: "sheet",
            version: 3
        };

        return updated;
    };

    const createDuplicateLayer = (dupeType, layerKey, layerOrder, newlayers) => {
        // get the layer we are duping from.
        let layers = JSON.parse(JSON.stringify(newlayers))
        let layer = layers.find(sl => sl.layerName === dupeType)
        if (layer) {
            let newLayer = { ...layer } // create a new layer object to avoid mutating the original one
            newLayer.layerOrder = layerOrder + 0.5 // old system has one less layer
            newLayer.parentLayerKey = layer.layerKey
            newLayer.layerKey = layerKey
            newLayer.layerHidden = 0
            newLayer.layerActive = 0 // the layer is for any slots that the user clicks edit on. They wont be on before that.

            // check name to avoid dupe names
            let incNum = 2
            let fixedName = newLayer.layerName
            if (Utility.endsWithNumber(fixedName)) {
                fixedName = fixedName.substring(0, fixedName.length - 1)
            }
            while (newlayers.find(sl => sl.layerName === fixedName)) { // eslint-disable-line no-loop-func
                fixedName = newLayer.layerName + incNum
                incNum++
                if (incNum > 100) {
                    break
                }

            }
            newLayer.layerName = fixedName

            return newLayer
        }

        return null
    }

    const combineDirectSvgs = svgs => {
        let combinedSvgs = '<svg id="" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="1000" height="1000">';
        svgs.forEach(svg => {
            if (svg) {
                if (svg.includes("-120, -120, 240, 240")) {
                    svg = svg.replace("-120, -120, 240, 240", "-100, -100, 200, 200");
                }
                if (svg.includes("0, 0, 240, 240")) {
                    const matrixIndex = svg.indexOf("<g transform");
                    if (matrixIndex > 0) {
                        svg = svg.replace("<g transform", '<g transform="matrix(1.2, 0, 0, 1.2, -24, -24)"><g transform');
                    }
                    const lastSvgTag = svg.lastIndexOf('</svg>');
                    svg = `${svg.substring(0, lastSvgTag)}</g></svg>`;
                }
            }
            combinedSvgs += svg;
        });
        combinedSvgs += '</svg>';

        const hash = Utility.cyrb53(combinedSvgs, 'snapcounter_sheet_v3');
        combinedSvgs = combinedSvgs.replace('id=""', `id="combined_${hash}"`);

        return combinedSvgs;
    };

    const removeSpacersActiveLayerValues = _alv => {
        let alv = { ..._alv }
        for (const [key] of Object.entries(alv)) {
            if (key.endsWith('_5') || key.endsWith('_59') || key.endsWith('_72')) {
                delete alv[key] // remove the useless deprecated 'spacer' entry
            }
        }

        return alv
    }

    const removeSpacersLayers = _layers => {
        let layers = [..._layers]
        // remove spacers from layer.inputs
        layers.forEach(ly => {
            ly.inputs = ly.inputs.filter(inp => inp.inputKey !== 5 && inp.inputKey !== 59 && inp.inputKey !== 72)
            ly.layerInputKeys = ly.layerInputKeys.filter(inputKey => inputKey !== 5 && inputKey !== 59 && inputKey !== 72)
        })

        return layers
    }

    return (
        <div>
            <div className={sheetOperationsOpen ? 'sheet-operations opened' : 'sheet-operations'}>

                <div className={sheetOperationsOpen ? 'controls-opener opened' : 'controls-opener closed'}>
                    <div className="title">sheet operations</div>
                    <button className={sheetOperationsOpen ? 'standard-button yellow closed' : 'standard-button yellow'} onClick={() => setSheetOperationsOpen(true)}>open</button>
                    <button className={sheetOperationsOpen ? 'standard-button red' : 'standard-button red closed'} onClick={() => setSheetOperationsOpen(false)}>close</button>
                </div>

                <div className={sheetOperationsOpen ? 'controls' : 'display-none'}>

                    <div className="controls-container">
                        <div className="options">
                            <div className="title">
                                <div className="popupable" data-popup="Save sheet"
                                    onClick={actions.popupInfoActive}>save sheet</div>
                            </div>
                            <div className="">
                                <input type="checkbox" onChange={() => setSaveOption('app')} id="in-app" checked={saveOption === 'app' ? 'checked' : ''} /> <label htmlFor="in-app">in app</label>
                                <input type="checkbox" onChange={() => setSaveOption('file')} id="to-file" checked={saveOption === 'file' ? 'checked' : ''} /> <label htmlFor="to-file">to file</label>
                            </div>
                        </div>
                        <div className="options">
                            <div className="title">
                                export sheet:
                            </div>
                            <div className="">
                                <input type="checkbox" onChange={() => setSaveOption('svg')} id="to-svg" checked={saveOption === 'svg' ? 'checked' : ''} /> <label htmlFor="to-svg">to svg</label>
                                <input type="checkbox" onChange={() => setSaveOption('png')} id="to-png" checked={saveOption === 'png' ? 'checked' : ''} /> <label htmlFor="to-png">to png</label>
                            </div>
                        </div>

                        <div className="actions">
                            <div className={saveActionsResult ? 'display-none' : ''}>
                                name: <input id="saveActionsSaveName" type="text" onChange={onSaveName} value={saveName} />
                                <button className={saveName ? 'standard-button blue' : 'standard-button disabled'}
                                    onClick={saveName ? saveSheet : null}
                                >submit</button>
                            </div>
                            <div className={saveActionsResult ? 'saved' : 'display-none'}>
                                <span>{saveActionsResult}</span>
                                <button className="standard-button yellow"
                                    onClick={saveActionsOk}
                                >ok</button>
                            </div>
                        </div>
                    </div>




                    <div className="controls-container load">
                        <div className="title-with-button">
                            <div className="popupable" data-popup="Load sheet"
                                onClick={actions.popupInfoActive}>load sheet</div>

                            <button className="standard-button yellow" onClick={clickClearSheet}>clear sheet</button>

                        </div>

                        {loadSheetErrorMessage === '' ?

                            <div className="load-from">

                                <span>load from:</span>
                                <input type="radio"
                                    onChange={() => onLoadFrom('app')}
                                    id="loadFrom_app"
                                    checked={loadFrom === 'app'}
                                    value="app"
                                    onClick={() => onLoadFrom('app')}
                                /> <label htmlFor="loadFrom_app">app</label>

                                <input type="radio"
                                    onChange={() => onLoadFrom('file')}
                                    id="loadFrom_file"
                                    checked={loadFrom === 'file'}
                                    value="file"
                                    onClick={() => onLoadFrom('file')}
                                /> <label htmlFor="loadFrom_file">file</label>

                                <div className="selects">
                                    {loadFrom === 'app' ?
                                        <div >
                                            <select id="loadSheet" value={selectedSheetHash} onChange={loadSheetFromApp}>
                                                <option key="none" value="">choose sheet</option>
                                                {savedSheetsMetadata ?
                                                    savedSheetsMetadata.map((ss, index) => <option key={index} value={ss.hash}>{ss.name}</option>)
                                                    : ''}
                                            </select>
                                        </div>
                                        : <div>
                                            <div className="load-from-file">
                                                <input type="file" id="sheet_file" accept=".scz" onChange={loadSheetFromFile} />
                                            </div>
                                        </div>}
                                </div>

                            </div>
                            :
                            <div className="error">
                                <div>{loadSheetErrorMessage}</div>
                                <button className="standard-button green" onClick={() => setLoadSheetErrorMessage('')}>Ok</button>
                            </div>
                        }

                    </div>

                    <div className="controls-container">
                        <div className="title">
                            <div className="popupable" data-popup="Cutting guides"
                                onClick={actions.popupInfoActive}>cutting guides</div>
                        </div>
                        <div className="options cutting-guides">



                            <input type="radio"
                                onChange={() => onChangeGuideType('none')}
                                id="cuttingGuide_none"
                                checked={sheetParameters.guideType === 'none'}
                                value="none"
                                onClick={() => onChangeGuideType('none')}
                            /> <label htmlFor="cuttingGuide_none">none</label>

                            <input type="radio"
                                onChange={() => onChangeGuideType('edges')}
                                id="cuttingGuide_edges"
                                checked={sheetParameters.guideType === 'edges'}
                                value="edges"
                                onClick={() => onChangeGuideType('edges')}
                            /> <label htmlFor="cuttingGuide_edges">edges</label>


                        </div>
                        <div className="options cutting-guides">

                            <input type="radio"
                                onChange={() => onChangeGuideType('corners')}
                                id="cuttingGuide_corners"
                                checked={sheetParameters.guideType === 'corners'}
                                value="corners"
                                onClick={() => onChangeGuideType('corners')}
                            /> <label htmlFor="cuttingGuide_corners">corners</label>

                            <input type="radio"
                                onChange={() => onChangeGuideType('lines')}
                                id="cuttingGuide_lines"
                                checked={sheetParameters.guideType === 'lines'}
                                value="lines"
                                onClick={() => onChangeGuideType('lines')}
                            /> <label htmlFor="cuttingGuide_lines">lines</label>


                        </div>
                    </div>






                    <div className="controls-container">
                        <div className="title-with-button">
                            <div className="popupable" data-popup="Printable area" onClick={evt => actions.popupInfoActive(evt)}>printable area</div>
                            <button className={printableAreaIsAtDefaults() ? 'standard-button disabled' : 'standard-button blue'} onClick={resetPrintableArea}>reset</button>
                        </div>
                        <div className="printable-area-settings">
                            <div><div>width:</div>  <input type="text" id="printableArea_width" className={validPrintableAreaWidth() ? '' : 'warning'} onChange={onChangePrintableArea} value={printableAreaMM_width} /> mm</div>
                            <div><div>length:</div> <input type="text" id="printableArea_height" className={validPrintableAreaHeight() ? '' : 'warning'} onChange={onChangePrintableArea} value={printableAreaMM_height} /> mm</div>
                        </div>
                    </div>











                </div>
            </div>
            <div className="temp-sheet">
                <svg id="build_sheet" width="1200" height="1553" />
            </div>
        </div>
    );
}
export default SheetOperations